import { MetricsTransactionDataModel } from '../../../../schemas/MetricsTransaction.schema';
import { OwnershipTimelineStack } from '../../../../schemas/OwnershipTimeline.schema';
import { SecurityReport } from '../../../../schemas/SecurityReport.schema';
import { isValidDate, plainDateToZonedDateTime } from '../../../../util/date-utilts';
import { FMT } from '../../../../util/formatter-service';
import { getClassOfLabel, getSecurityClass } from './security-class-model';

/**
 * Derivations behind the FD-4344 Equity & Ownership Holdings card.
 *
 * Rows come from `/metrics/securityReport`, which returns one row per lot — a (restructure, fund)
 * pair — already filtered to a company and an as-of date. Two things it does not carry are joined in
 * from the ownership-timeline response on `restructureId`: the fully diluted share count (for
 * ownership %) and the security's display name.
 *
 * Every `SecurityReport` field is optional in the schema (no `.required()`), so each is defaulted
 * here rather than trusted.
 */

/** Lifecycle chip shown on a Security History row. `active` shows no chip. */
export type LifecycleState = 'active' | 'partiallyRealized' | 'fullyRealized' | 'writtenOff' | 'converted';

export const LifecycleLabels: Record<Exclude<LifecycleState, 'active'>, string> = {
  partiallyRealized: 'Partially Realized',
  fullyRealized: 'Fully Realized',
  writtenOff: 'Written Off',
  converted: 'Converted',
};

export interface IEquityLot {
  /** Stable row id: one lot is one (restructure, fund) pair. */
  id: string;
  restructureId: string;
  fundId: number | null;
  /** Specific security, e.g. "Series A-1 Pref 2022-09 restr." */
  securityName: string;
  /** Instrument as the API describes it, e.g. "Equity", "Common Stock". Not a grouping key. */
  securityType: string;
  /** Round class, e.g. "Series A-1 Preferred" — both trees' top level. See `security-class-model`. */
  securityClass: string;
  /** The position this lot converted from, when it converted. */
  initialHolding: string | null;
  investmentDate: Date | null;
  shares: number;
  invested: number;
  fmv: number;
  proceeds: number;
  /**
   * This lot's own ownership — its fund's slice — in percent units, derived from the lot's own share
   * count. `null` when there is no diluted count to divide by.
   */
  ownershipPct: number | null;
  /**
   * Whole-security ownership from the joined stack, in percent units.
   *
   * The backend aggregates a stack by `restructureId` with no fund split, so this is the same value on
   * every lot of a security. It must therefore be counted **once per security, never once per lot** —
   * see `getOwnershipTotal`.
   */
  securityOwnershipPct: number | null;
  /** `undefined` when nothing was invested, so the multiplier formatter renders a dash. */
  moic: number | undefined;
  lifecycle: LifecycleState;
}

/**
 * MOIC counts realized proceeds alongside the current mark, so a fully exited lot still shows the
 * multiple it returned rather than 0. Matches the POC: (FMV + proceeds) ÷ invested.
 */
export function getMoic(fmv: number, proceeds: number, invested: number): number | undefined {
  if (invested <= 0) {
    return undefined;
  }
  return (fmv + proceeds) / invested;
}

/**
 * Lifecycle is not a field on `securityReport` — it is read off the numbers.
 *
 * A lot holding nothing, worth nothing and with nothing returned either converted into another
 * security (its money was carried) or was written off; `initialHolding` naming a different position
 * is what separates the two.
 *
 * Both sides of that comparison are normalised first. `initialHolding` arrives decorated like
 * `currentHolding` ("Unbabel, Inc. (Common Stock 10/10/2019 (A))") whereas `securityType` has already
 * been reduced to "Common Stock", so comparing them raw is unequal even when they name the same
 * position — which would label every write-off as a conversion.
 */
export function getLifecycleState(lot: {
  shares: number;
  fmv: number;
  proceeds: number;
  securityType: string;
  initialHolding: string | null;
}): LifecycleState {
  const { shares, fmv, proceeds, securityType, initialHolding } = lot;
  const holdsNothing = shares <= 0;

  if (holdsNothing && proceeds <= 0 && fmv <= 0) {
    return isConversion(securityType, initialHolding) ? 'converted' : 'writtenOff';
  }
  if (holdsNothing && proceeds > 0) {
    return 'fullyRealized';
  }
  if (proceeds > 0) {
    return 'partiallyRealized';
  }
  return 'active';
}

/**
 * The name to show for a security, out of the decorated string the API sends.
 *
 * Two things have to come off it, and neither is part of a name:
 *
 * 1. **The restructure id.** `getOwnershipTimeline` builds a stack's label as
 *    `${restructureId}.${position}`, so every joined name arrives prefixed with the id — which is
 *    where rows reading "061518.01.5998…" come from. It is the same leak the card was asked to close.
 * 2. **The company.** What is left is "Bird, Inc. (Series C Preferred Stock 06/15/2018)", and the
 *    company is named at the top of the page; repeating it on every row costs the width the class and
 *    the date need.
 *
 * What survives is "Series C Preferred Stock 06/15/2018" — the security and the period that
 * distinguishes it from the next buy, which is what the design labels a row with. Anything that does
 * not match those shapes passes through untouched rather than being mangled.
 */
export function toDisplayName(name: string, restructureId: string): string {
  const prefix = `${restructureId}.`;
  const withoutId = restructureId !== '' && name.startsWith(prefix) ? name.slice(prefix.length) : name;
  // Greedy to the trailing paren, matching getSecurityTypeFromLabel, so a tranche's own
  // parenthetical — "(Common Stock 10/10/2019 (A))" — survives inside the capture.
  const withoutCompany = /\(([\s\S]+)\)\s*$/.exec(withoutId)?.[1] ?? withoutId;
  const trimmed = withoutCompany.trim();
  return trimmed === '' ? withoutId.trim() : trimmed;
}

/**
 * Recovers a groupable security type out of the decorated string the API sends.
 *
 * `position` is populated but is not a bare type — it carries the company, the issue date and a
 * tranche letter, so it is unique per lot:
 *
 *   "Unbabel, Inc. (Common Stock 10/10/2019 (A))"   -> "Common Stock"
 *   "Unbabel, Inc. (Common Stock 10/10/2019 (B))"   -> "Common Stock"
 *   "Unbabel, Inc. (Series C Preferred Stock 09/18/2019)" -> "Series C Preferred Stock"
 *
 * Grouping on it directly gives every lot its own single-child type and nothing is expandable, which
 * is the whole point of the Overview tree. So the type is the text inside the outer parentheses up to
 * the issue date.
 *
 * This is pattern-matching on a display string, which is fragile by nature: it is bounded to that one
 * shape, returns null rather than guessing when the shape does not match, and callers fall back to the
 * undecorated string. The durable fix is a real security-type field on the response — see the spec.
 */
export function getSecurityTypeFromLabel(label: string): string | null {
  const inner = /\(([\s\S]+)\)\s*$/.exec(label)?.[1];
  if (inner === undefined) {
    return null;
  }
  // Everything up to the issue date; without a date the whole parenthetical is the type.
  const type = inner.split(/\s+\d{1,2}\/\d{1,2}\/\d{2,4}/)[0].trim();
  return type === '' ? null : type;
}

/**
 * Whether a lot's capital came from a different position, i.e. it converted rather than being written
 * off. Both names are reduced to their bare type before comparing, since they arrive in different
 * shapes.
 */
function isConversion(securityType: string, initialHolding: string | null): boolean {
  if (initialHolding === null || initialHolding === '') {
    return false;
  }
  const from = getSecurityTypeFromLabel(initialHolding) ?? initialHolding;
  return from !== securityType;
}

/**
 * Transaction types that state a lot's lifecycle outright, so it does not have to be inferred.
 *
 * `securityReport` is an aggregation over these transactions and flattens the distinction away, but
 * every one of them reaches the frontend on `companyMetricsAtomJ`. Reading them is both simpler and
 * more reliable than the numeric inference: that inference keys on `shares <= 0`, and `sharesHeld` is
 * the sum of a `quantity` this client's ledger leaves empty, so it reads every lot as holding nothing.
 */
const WriteOffTransType = 'Investment - Write Off';

/** Sales and returns of capital — each with its own date, quantity and amount. */
export const ProceedsTransTypes = new Set([
  'Investment - Sale',
  'Investment - Proceeds',
  'Investment - Stock Proceeds',
  'Investment - Return of Capital',
]);

/** One lot is one (restructure, fund) pair — the same grouping `securityReport` uses server-side. */
export function lotKeyOf(restructureId: string | null | undefined, fundId: number | null): string {
  return `${restructureId ?? ''}:${fundId ?? 'no-fund'}`;
}

export function groupTransactionsByLot(
  transactions: MetricsTransactionDataModel[]
): Map<string, MetricsTransactionDataModel[]> {
  return transactions.reduce((acc, transaction) => {
    if (!transaction.restructureId) {
      return acc;
    }
    const key = lotKeyOf(transaction.restructureId, transaction.fundId ?? null);
    const existing = acc.get(key);
    if (existing === undefined) {
      acc.set(key, [transaction]);
    } else {
      existing.push(transaction);
    }
    return acc;
  }, new Map<string, MetricsTransactionDataModel[]>());
}

/**
 * Lifecycle read off the transactions, or `null` when they cannot settle it and the numeric inference
 * should be used instead.
 *
 * Only a write-off is taken from the transactions. It is unambiguous — the position was written off —
 * and it does not change whether the lot is listed, only how it is labelled.
 *
 * `Investment - Restructure Out` is deliberately NOT read as a conversion. A restructure-out records
 * that a position moved between restructure ids, not that this lot is finished: a lot can have one and
 * still hold shares afterwards. Treating it as a conversion classified most of a real portfolio as
 * converted, and because `selectHeldLots` excludes converted lots, that silently emptied Security
 * Overview. Deciding conversion properly needs to pair a restructure-out against the matching
 * restructure-in and confirm nothing remains — which needs the share counts this API leaves empty.
 * Until then conversion stays with the numeric inference, whose failure mode is a wrong chip rather
 * than a disappearing holding.
 */
export function getLifecycleFromTransactions(
  transactions: MetricsTransactionDataModel[] | undefined
): LifecycleState | null {
  if (transactions === undefined || transactions.length === 0) {
    return null;
  }
  // Confirmed only. A pending transaction is an entry, not a fact — the app treats them as
  // provisional elsewhere (`hasPendingTransactionImpact` shows a warning icon rather than applying the
  // change), so an unconfirmed write-off must not label a lot as written off.
  if (transactions.some((t) => t.status === 'Confirmed' && t.transType === WriteOffTransType)) {
    return 'writtenOff';
  }
  return null;
}

export interface IToEquityLotsParams {
  reports: SecurityReport[];
  /** Ownership-timeline stacks keyed by `restructureId`, for the display name. */
  stacksByRestructureId: Map<string, OwnershipTimelineStack>;
  /** Company-wide fully diluted shares as of the same date. `null` leaves ownership % unknown. */
  fullyDilutedShares: number | null;
  /**
   * Transactions keyed by lot, from `companyMetricsAtomJ`. Optional: the card still works without them,
   * falling back to inferring lifecycle from the numbers.
   */
  transactionsByLot?: Map<string, MetricsTransactionDataModel[]>;
}

/**
 * Shown when a lot has no name anywhere. The backend derives `currentHolding` from a transaction's
 * `position` and falls back to `''`, so a lot whose transactions carry no position arrives genuinely
 * unnamed rather than absent.
 *
 * Said in words rather than with a dash: these rows can carry real money, and "—" beside a nine-figure
 * FMV reads as a rendering fault. "Unnamed security" says what is true — the ledger has no position on
 * any of this lot's transactions — and points at the fix, which is in the data.
 */
export const UnnamedSecurity = 'Unnamed security';

/** First value with actual content. `??` is not enough: the API sends empty strings, not nulls. */
function firstNamed(...values: (string | null | undefined)[]): string {
  return values.find((value) => value != null && value.trim() !== '')?.trim() ?? '';
}

export function toEquityLots({
  reports,
  stacksByRestructureId,
  fullyDilutedShares,
  transactionsByLot,
}: IToEquityLotsParams): IEquityLot[] {
  return reports.map((report, index) => {
    const restructureId = report.restructureId ?? '';
    const stack = stacksByRestructureId.get(restructureId);
    const fundId = report.fundId ?? null;
    // `position` is the type ("Common Stock", "Series A Preferred") and `label` names the individual
    // lot — the same split FD-4393's holdings grid draws as "Security Type" and "Lot Name".
    // `currentHolding` is NOT a type despite its "Position" label: it varies per restructure, so
    // grouping on it gives every lot its own single-child type.
    // The restructure id is deliberately not a fallback: it is an internal key, and a row labelled
    // "48359" identifies nothing to a reader.
    const securityName = firstNamed(
      toDisplayName(firstNamed(stack?.label, report.currentHolding, stack?.position), restructureId),
      UnnamedSecurity
    );
    // `position` describes the security but is decorated per lot, so the groupable type is read out
    // of it; `currentHolding` carries the same shape and stands in when there is no stack.
    const describedType = firstNamed(stack?.position, report.currentHolding, securityName);
    const securityType = firstNamed(getSecurityTypeFromLabel(describedType), describedType);
    // The round comes off the name — the type is the instrument, which for most clients is just
    // "Equity" or "Debt" and would put every series in one band.
    const securityClass = getSecurityClass(securityName, securityType).label;
    const initialHolding = report.initialHolding ?? null;
    const shares = report.sharesHeld ?? 0;
    const invested = report.investmentAmount ?? 0;
    const fmv = report.fmv ?? 0;
    const proceeds = report.realizedValue ?? 0;

    return {
      // Fall back to the index so rows stay distinct even if the API omits restructureId.
      id: restructureId === '' ? `row-${index}` : lotKeyOf(restructureId, fundId),
      restructureId,
      fundId,
      securityName,
      securityType,
      securityClass,
      initialHolding,
      investmentDate: toDate(report.initialInvestmentDate),
      shares,
      invested,
      fmv,
      proceeds,
      ownershipPct: getOwnershipPct(shares, fullyDilutedShares),
      securityOwnershipPct: stack?.ownershipPct == null ? null : stack.ownershipPct * 100,
      moic: getMoic(fmv, proceeds, invested),
      // Stated lifecycle wins over the inferred one — see `getLifecycleFromTransactions`.
      lifecycle:
        getLifecycleFromTransactions(transactionsByLot?.get(lotKeyOf(restructureId, fundId))) ??
        getLifecycleState({ shares, fmv, proceeds, securityType, initialHolding }),
    };
  });
}

/**
 * `SecurityReport.initialInvestmentDate` is declared `yup.date()`, but `getSecurityReport` casts the
 * response rather than validating it through the schema, so at runtime the field is whatever JSON
 * carried — a string. Coerced here so nothing downstream has to guess.
 *
 * Plain `YYYY-MM-DD` strings go through `plainDateToZonedDateTime`, which builds the date in local
 * time; `new Date('2019-06-01')` would parse as UTC midnight and read as the previous day west of
 * Greenwich.
 */
export function toDate(value: Date | string | null | undefined): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }
  if (typeof value !== 'string' || value === '') {
    return null;
  }
  const parsed = plainDateToZonedDateTime(value);
  return isValidDate(parsed) ? parsed : null;
}

/**
 * A lot's own ownership in percent units, from its own share count. `securityReport` reports
 * `sharesHeld` per fund slice, so this is per-fund and safe to sum across lots.
 */
export function getOwnershipPct(shares: number, fullyDilutedShares: number | null): number | null {
  if (fullyDilutedShares === null || fullyDilutedShares <= 0) {
    return null;
  }
  return (shares / fullyDilutedShares) * 100;
}

/**
 * Total ownership across lots, in percent units.
 *
 * Two sources have to be combined without double counting. The joined stack's figure is authoritative
 * but describes a whole security, and a security split across N funds yields N lot rows that all carry
 * it — summing per lot would report that security's stake N times, which is how the "Current Ownership"
 * KPI could exceed 100%. So the stack figure is taken **once per `restructureId`**, and only securities
 * with no stack fall back to summing their lots' own per-fund slices.
 *
 * Known limitation: with a fund filter applied, a security we hold through several funds still reports
 * its whole-security stake, because splitting it needs per-fund share counts and `sharesHeld` is empty
 * for some clients (see the spec's gap 1). Overstating a filtered view is preferred to reporting 0%.
 */
export function getOwnershipTotal(lots: IEquityLot[]): number | null {
  const securityFigures = new Map<string, number>();
  const perLotFallback = new Map<string, number | null>();

  for (const lot of lots) {
    const key = lot.restructureId === '' ? lot.id : lot.restructureId;
    if (lot.securityOwnershipPct !== null) {
      securityFigures.set(key, lot.securityOwnershipPct);
      continue;
    }
    if (lot.ownershipPct !== null) {
      perLotFallback.set(key, (perLotFallback.get(key) ?? 0) + lot.ownershipPct);
    }
  }

  let total: number | null = null;
  for (const value of securityFigures.values()) {
    total = (total ?? 0) + value;
  }
  for (const [key, value] of perLotFallback) {
    // A security with a stack figure is already counted; its lots must not be added again.
    if (securityFigures.has(key) || value === null) {
      continue;
    }
    total = (total ?? 0) + value;
  }
  return total;
}

export function selectHeldLots(lots: IEquityLot[]): IEquityLot[] {
  return lots.filter(
    (lot) =>
      // A converted lot's capital was carried into another security, which is listed in its own
      // right — counting both would show the same money twice.
      lot.lifecycle !== 'converted' &&
      (lot.shares > 0 || lot.fmv > 0 || (lot.invested > 0 && lot.proceeds <= 0))
  );
}

export function selectLotsForFund(lots: IEquityLot[], fundId: number | null): IEquityLot[] {
  if (fundId === null) {
    return lots;
  }
  return lots.filter((lot) => lot.fundId === fundId);
}

/**
 * Lots bought on or before the as-of date.
 *
 * `securityReport` is asked for the same date and is expected to have applied it, so on a correct
 * response this changes nothing. It is applied anyway because the as-of date has to hold across the
 * whole card — the transactions behind the chains and the chips come from `companyMetricsAtomJ`,
 * which is not date-scoped at all — and a lot dated after the as-of date must not appear in a
 * grouping or a total while its events are being filtered out of the chain beside it.
 *
 * An undated lot is kept: there is nothing to compare, and dropping it would silently lose a holding.
 */
export function selectLotsAsOf(lots: IEquityLot[], asOfDate: Date | null): IEquityLot[] {
  if (asOfDate === null || !isValidDate(asOfDate)) {
    return lots;
  }
  const cutoff = endOfDay(asOfDate);
  return lots.filter((lot) => lot.investmentDate === null || lot.investmentDate.getTime() <= cutoff);
}

/**
 * Transactions dated on or before the as-of date, keeping the undated ones for the same reason
 * `selectLotsAsOf` keeps undated lots.
 */
export function selectTransactionsAsOf(
  transactions: MetricsTransactionDataModel[],
  asOfDate: Date | null
): MetricsTransactionDataModel[] {
  if (asOfDate === null || !isValidDate(asOfDate)) {
    return transactions;
  }
  const cutoff = endOfDay(asOfDate);
  return transactions.filter((transaction) => {
    const date = toDate(transaction.transactionDate);
    return date === null || date.getTime() <= cutoff;
  });
}

/** The as-of date is a day, not an instant: an event on that day is included whatever its time. */
function endOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();
}

export interface IEquityTotals {
  ownershipPct: number | null;
  shares: number;
  invested: number;
  proceeds: number;
  fmv: number;
  moic: number | undefined;
  activeLotCount: number;
}

/**
 * Totals over exactly the lots given — every figure from the same set, so a caller's total always
 * reconciles against the rows it is showing.
 *
 * It previously summed shares and FMV over `selectHeldLots(lots)` while summing invested and proceeds
 * over all of `lots`. Two callers passing different sets then produced different "Invested" under the
 * same label, one row apart, and a MOIC dividing held-only FMV by all-lot invested. Scoping is the
 * caller's decision now: pass the rows you are totalling.
 *
 * `activeLotCount` is the one exception, and deliberately so — "Active Lots" counts currently-held
 * positions regardless of the set handed in.
 */
export function aggregateTotals(lots: IEquityLot[]): IEquityTotals {
  const shares = sumBy(lots, (lot) => lot.shares);
  const fmv = sumBy(lots, (lot) => lot.fmv);
  const invested = sumBy(lots, (lot) => lot.invested);
  const proceeds = sumBy(lots, (lot) => lot.proceeds);
  const ownership = getOwnershipTotal(lots);

  return {
    ownershipPct: ownership,
    shares,
    invested,
    proceeds,
    fmv,
    moic: getMoic(fmv, proceeds, invested),
    activeLotCount: selectHeldLots(lots).length,
  };
}

function sumBy(lots: IEquityLot[], pick: (lot: IEquityLot) => number): number {
  return lots.reduce((total, lot) => total + pick(lot), 0);
}

/**
 * A buying block: one security bought on one date, labelled by the security's own name.
 *
 * The name is what the design shows — "SAFE 2021-03", "Series A-1 Pref 2022-09 restr." — with the
 * date in its own column beside it. `buildClassPaths` appends the date only where two blocks of one
 * class share a name and would otherwise collapse into each other.
 */
export function blockLabelOf(lot: IEquityLot): string {
  return lot.securityName;
}

function datedBlockLabel(lot: IEquityLot): string {
  const date = lot.investmentDate === null ? '' : FMT.format('date', lot.investmentDate);
  return date === '' ? lot.securityName : `${lot.securityName} — ${date}`;
}

/**
 * The tree path both grids use: round → buying block → fund.
 *
 * The round is the band — Series A holds its A-1 tranche as a row rather than a band of its own,
 * which is what "all Series A rows sit under a single Series A group" asks for. The class row always
 * exists, even holding a single block, because it is the level a reader traces a position by. The one
 * exception is an *unplaceable* class holding one block, which would otherwise draw a band and a
 * child row saying nearly the same thing; those stay flat.
 *
 * The fund level appears only where a block was bought by more than one fund — a single-fund block
 * already names its fund in the Fund column.
 */
export function buildClassPaths(lots: IEquityLot[]): Map<string, string[]> {
  const lotCountByBlock = new Map<string, number>();
  const blocksByGroup = new Map<string, Set<string>>();
  const namesByGroup = new Map<string, Map<string, Set<string>>>();

  for (const lot of lots) {
    const group = groupOf(lot);
    const block = blockKeyOf(lot);
    lotCountByBlock.set(block, (lotCountByBlock.get(block) ?? 0) + 1);
    blocksByGroup.set(group, (blocksByGroup.get(group) ?? new Set<string>()).add(block));

    const names = namesByGroup.get(group) ?? new Map<string, Set<string>>();
    names.set(lot.securityName, (names.get(lot.securityName) ?? new Set<string>()).add(block));
    namesByGroup.set(group, names);
  }

  return lots.reduce((paths, lot) => {
    const group = groupOf(lot);
    // Two blocks of one round sharing a name would collapse into each other, so those take the date.
    const shared = (namesByGroup.get(group)?.get(lot.securityName)?.size ?? 0) > 1;
    const label = shared ? datedBlockLabel(lot) : blockLabelOf(lot);
    const path = needsClassRow(lot, group, blocksByGroup) ? [group, label] : [label];
    if ((lotCountByBlock.get(blockKeyOf(lot)) ?? 0) > 1) {
      path.push(lot.id);
    }
    return paths.set(lot.id, path);
  }, new Map<string, string[]>());
}

/** The band a lot sits in: its round, not its tranche. */
export function groupOf(lot: IEquityLot): string {
  return getClassOfLabel(lot.securityClass).groupLabel;
}

/**
 * A round always gets its band, whatever it holds — that is the level a reader traces a position by.
 *
 * A class the parser could not place is a different matter: it is labelled with the security's own
 * text, so a single-block one would draw a band and a block row saying nearly the same thing. Those
 * stay flat: a band whose only child repeats it helps nobody.
 */
function needsClassRow(lot: IEquityLot, group: string, blocksByGroup: Map<string, Set<string>>): boolean {
  return getClassOfLabel(lot.securityClass).kind !== 'other' || (blocksByGroup.get(group)?.size ?? 0) > 1;
}

/** A block is one security bought on one date; the date keeps two buys of one name apart. */
function blockKeyOf(lot: IEquityLot): string {
  return `${groupOf(lot)} ${datedBlockLabel(lot)}`;
}

/**
 * AG Grid builds tree groups in row order, so the rows have to arrive sorted.
 *
 * Classes follow the round sequence — pre-seed, seed, the lettered series, common, then SAFEs and
 * notes at the bottom. Ordering them by date instead drops a late SAFE between two rounds, since a
 * SAFE is bought before the round it converts into. Within a class, blocks run oldest first; undated
 * blocks sort last, so a missing date cannot drag one to the front.
 */
export function sortLotsByClass(lots: IEquityLot[]): IEquityLot[] {
  const blockRank = lots.reduce((acc, lot) => {
    const key = blockKeyOf(lot);
    const time = timeOf(lot.investmentDate);
    const current = acc.get(key);
    return acc.set(key, current === undefined ? time : Math.min(current, time));
  }, new Map<string, number>());

  return [...lots].sort((a, b) => {
    const byClass = getClassOfLabel(a.securityClass).rank - getClassOfLabel(b.securityClass).rank;
    if (byClass !== 0) {
      return byClass;
    }
    // Unplaced classes all share one rank, so they still need an order of their own.
    if (groupOf(a) !== groupOf(b)) {
      return groupOf(a).localeCompare(groupOf(b));
    }
    const byBlock = rankOf(blockRank, blockKeyOf(a)) - rankOf(blockRank, blockKeyOf(b));
    if (byBlock !== 0) {
      return byBlock;
    }
    if (a.securityName !== b.securityName) {
      return a.securityName.localeCompare(b.securityName);
    }
    return (a.fundId ?? 0) - (b.fundId ?? 0);
  });
}

function rankOf(ranks: Map<string, number>, key: string): number {
  return ranks.get(key) ?? Number.MAX_SAFE_INTEGER;
}

/** Undated lots sort last rather than at the epoch. */
function timeOf(date: Date | null): number {
  return date === null ? Number.MAX_SAFE_INTEGER : date.getTime();
}

/** Distinct fund ids in document order, for the Fund cell's "first fund +N" rendering. */
export function getDistinctFundIds(lots: IEquityLot[]): number[] {
  const seen = new Set<number>();
  for (const lot of lots) {
    if (lot.fundId !== null) {
      seen.add(lot.fundId);
    }
  }
  return [...seen];
}
